import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  ShoppingCart, User, LogOut, Shield, Gavel, Home, Clock, ChevronDown, ChevronUp,
  Edit2, Save, X, Plus, Minus, RefreshCw
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_91be65d6-9fee-4eda-abde-3fd83cab62ae/artifacts/kxr3j7eu_IMG_7793.png";

export default function AdminBids() {
  const { logout, getAuthHeader } = useAuth();
  const { itemCount } = useCart();
  const [auctionData, setAuctionData] = useState([]);
  const [needsReauctionData, setNeedsReauctionData] = useState([]);
  const [autoBidsData, setAutoBidsData] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedProduct, setExpandedProduct] = useState(null);
  const [editingAuction, setEditingAuction] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [reauctionForm, setReauctionForm] = useState({});

  useEffect(() => {
    fetchData();
    // Refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const [bidsResponse, reauctionResponse, autoBidsResponse] = await Promise.all([
        axios.get(`${API}/admin/all-bids`, { headers: getAuthHeader() }),
        axios.get(`${API}/admin/products/needs-reauction`, { headers: getAuthHeader() }),
        axios.get(`${API}/admin/auctions/all-auto-bids`, { headers: getAuthHeader() }).catch(() => ({ data: { products: [] } }))
      ]);
      setAuctionData(bidsResponse.data.products_with_bids || []);
      setNeedsReauctionData(reauctionResponse.data.products || []);
      
      // Index auto-bids by product_id for easy lookup
      const autoBidsMap = {};
      (autoBidsResponse.data.products || []).forEach(p => {
        autoBidsMap[p.product_id] = p.auto_bids || [];
      });
      setAutoBidsData(autoBidsMap);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const getTimeRemaining = (endTime) => {
    if (!endTime) return 'NO END TIME';
    const now = new Date();
    const end = new Date(endTime);
    const diff = end - now;
    
    if (diff <= 0) return 'ENDED';
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const formatEndTime = (endTime) => {
    if (!endTime) return 'NOT SET';
    const date = new Date(endTime);
    return date.toLocaleString('en-US', {
      timeZone: 'America/New_York',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).toUpperCase() + ' EST';
  };

  // Helper to format date for datetime-local input (local timezone)
  const formatForDateTimeInput = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const toggleExpanded = (productId) => {
    setExpandedProduct(expandedProduct === productId ? null : productId);
  };

  const startEditing = (item) => {
    // Convert bid_end_time to local datetime-local format for editing
    let endTimeLocal = '';
    if (item.bid_end_time) {
      const date = new Date(item.bid_end_time);
      endTimeLocal = formatForDateTimeInput(date);
    }
    
    setEditingAuction(item.product_id);
    setEditForm({
      reserve_price: item.reserve_price || '',
      buy_now_price: item.buy_now_price || '',
      bid_end_time: endTimeLocal
    });
  };

  const cancelEditing = () => {
    setEditingAuction(null);
    setEditForm({});
  };

  const addTime = (minutes) => {
    const currentEnd = editForm.bid_end_time ? new Date(editForm.bid_end_time) : new Date();
    currentEnd.setMinutes(currentEnd.getMinutes() + minutes);
    setEditForm(prev => ({
      ...prev,
      bid_end_time: formatForDateTimeInput(currentEnd)
    }));
  };

  const saveAuctionChanges = async (productId) => {
    try {
      // Build update object
      const updates = {};
      if (editForm.reserve_price !== '') updates.reserve_price = parseFloat(editForm.reserve_price);
      if (editForm.buy_now_price !== '') updates.buy_now_price = parseFloat(editForm.buy_now_price);
      if (editForm.bid_end_time) {
        // Convert local time to ISO string
        const localDate = new Date(editForm.bid_end_time);
        updates.bid_end_time = localDate.toISOString();
      }

      await axios.put(`${API}/admin/products/${productId}`, updates, {
        headers: getAuthHeader()
      });

      toast.success('Auction updated');
      setEditingAuction(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update auction');
    }
  };

  const endAuctionNow = async (productId, productName) => {
    if (!window.confirm(`END AUCTION FOR "${productName.toUpperCase()}" IMMEDIATELY? THIS WILL PROCESS THE WINNER IF RESERVE IS MET.`)) return;
    
    try {
      // Set end time to now
      await axios.put(`${API}/admin/products/${productId}`, {
        bid_end_time: new Date().toISOString()
      }, {
        headers: getAuthHeader()
      });

      // Trigger auction processing
      await axios.post(`${API}/admin/process-auctions`, {}, {
        headers: getAuthHeader()
      });

      toast.success('Auction ended and processed');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to end auction');
    }
  };

  const restartAuction = async (item) => {
    const hours = window.prompt('How many hours should the auction run?', '24');
    if (!hours || isNaN(hours)) return;

    try {
      const endTime = new Date();
      endTime.setHours(endTime.getHours() + parseInt(hours));

      await axios.put(`${API}/admin/products/${item.product_id}`, {
        bid_end_time: endTime.toISOString(),
        is_bidding: true
      }, {
        headers: getAuthHeader()
      });

      // Reset auction processed status
      await axios.post(`${API}/admin/reset-auction/${item.product_id}`, {}, {
        headers: getAuthHeader()
      });

      toast.success(`Auction restarted for ${hours} hours`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restart auction');
    }
  };

  const handleReauction = async (productId) => {
    const hours = reauctionForm[productId]?.hours || 24;
    
    try {
      await axios.post(`${API}/admin/products/${productId}/reauction`, {
        hours: parseInt(hours),
        reserve_price: reauctionForm[productId]?.reserve_price ? parseFloat(reauctionForm[productId].reserve_price) : undefined,
        buy_now_price: reauctionForm[productId]?.buy_now_price ? parseFloat(reauctionForm[productId].buy_now_price) : undefined
      }, {
        headers: getAuthHeader()
      });

      toast.success('Item re-auctioned successfully');
      setReauctionForm(prev => ({ ...prev, [productId]: undefined }));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to re-auction item');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-tiffany-500 font-mono cursor-blink">LOADING AUCTIONS</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 border-b border-zinc-800 bg-[#050505]/90 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/catalog">
              <img src={LOGO_URL} alt="BLK MRKT" className="h-8 w-auto" />
            </Link>
            <Badge className="bg-tiffany-500 text-black rounded-none border-0 hidden sm:inline-flex">ADMIN</Badge>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/catalog">
              <Button variant="ghost" size="icon" className="text-zinc-400 hover:text-white h-8 w-8 p-0">
                <Home className="w-5 h-5" />
              </Button>
            </Link>
            <Link to="/cart">
              <Button variant="ghost" size="icon" className="text-zinc-400 hover:text-white relative h-8 w-8 p-0">
                <ShoppingCart className="w-5 h-5" />
                {itemCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-tiffany-500 text-black text-xs w-4 h-4 flex items-center justify-center">
                    {itemCount}
                  </span>
                )}
              </Button>
            </Link>
            <Link to="/dashboard">
              <Button variant="ghost" size="icon" className="text-zinc-400 hover:text-white h-8 w-8 p-0">
                <User className="w-5 h-5" />
              </Button>
            </Link>
            <Link to="/admin">
              <Button variant="ghost" size="icon" className="text-zinc-400 hover:text-white h-8 w-8 p-0">
                <Shield className="w-5 h-5" />
              </Button>
            </Link>
            <Button variant="ghost" size="icon" onClick={logout} className="text-zinc-400 hover:text-white h-8 w-8 p-0">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="pt-24 pb-12 px-4 md:px-8">
        <div className="max-w-4xl mx-auto">
          <Link to="/admin" className="text-zinc-500 hover:text-white font-mono text-sm mb-6 inline-block">
            ← BACK TO ADMIN
          </Link>
          
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-8">
            <div className="flex items-center gap-3">
              <Gavel className="w-8 h-8 text-tiffany-500" />
              <h1 className="font-sans text-2xl sm:text-3xl text-white tracking-tighter">
                AUCTIONS
              </h1>
              {needsReauctionData.length > 0 && (
                <div className="flex items-center gap-1 bg-yellow-500/20 px-2 py-1">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse" />
                  <span className="font-mono text-xs text-yellow-500">{needsReauctionData.length} NEED ACTION</span>
                </div>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchData}
              className="border-zinc-700 text-zinc-400 hover:text-white rounded-none"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              REFRESH
            </Button>
          </div>

          {/* Active Auctions Section */}
          <div className="mb-8">
            <h2 className="font-mono text-lg text-tiffany-500 mb-4">ACTIVE ({auctionData.length})</h2>
          {auctionData.length === 0 ? (
            <div className="text-center py-12 border border-zinc-800">
              <Gavel className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
              <p className="font-mono text-zinc-500">No active auctions</p>
            </div>
          ) : (
            <div className="space-y-4">
              {auctionData.map((item) => {
                const timeRemaining = getTimeRemaining(item.bid_end_time);
                const isEnded = timeRemaining === 'ENDED';
                const isExpanded = expandedProduct === item.product_id;
                const isEditing = editingAuction === item.product_id;
                
                return (
                  <div 
                    key={item.product_id} 
                    className={`border ${isEnded ? 'border-red-500/30 bg-red-500/5' : 'border-zinc-800'}`}
                  >
                    <div className="p-4">
                      <div className="flex items-start gap-4">
                        {item.product_image && (
                          <img 
                            src={item.product_image} 
                            alt={item.product_name}
                            className="w-20 h-20 object-cover"
                          />
                        )}
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <Link 
                              to={`/product/${item.product_id}`}
                              className="font-mono text-lg text-white hover:text-tiffany-500"
                            >
                              {item.product_name}
                            </Link>
                            <Badge className={`${isEnded ? 'bg-red-500/20 text-red-500' : 'bg-tiffany-500/20 text-tiffany-500'} rounded-none border-0`}>
                              {isEnded ? 'ENDED' : 'LIVE'}
                            </Badge>
                          </div>
                          
                          {/* Auction Stats */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                            <div>
                              <p className="font-mono text-xs text-zinc-500">HIGH BID</p>
                              <p className="font-mono text-tiffany-500 text-lg">${item.highest_bid?.toFixed(2) || '0.00'}</p>
                            </div>
                            <div>
                              <p className="font-mono text-xs text-zinc-500">HIGH BIDDER</p>
                              <p className="font-mono text-white text-sm truncate" title={item.highest_bidder_email}>
                                {item.highest_bidder_name || 'NO BIDS'}
                              </p>
                              <p className="font-mono text-tiffany-500 text-xs truncate">
                                {item.highest_bidder_email}
                              </p>
                            </div>
                            <div>
                              <p className="font-mono text-xs text-zinc-500">TOTAL BIDS</p>
                              <p className="font-mono text-white">{item.total_bids}</p>
                            </div>
                            <div>
                              <p className="font-mono text-xs text-zinc-500">TIME LEFT</p>
                              <p className={`font-mono flex items-center gap-1 ${isEnded ? 'text-red-500' : 'text-yellow-500'}`}>
                                <Clock className="w-3 h-3" />
                                {timeRemaining}
                              </p>
                              <p className="font-mono text-xs text-zinc-600">
                                {formatEndTime(item.bid_end_time)}
                              </p>
                            </div>
                          </div>
                          
                          {/* Reserve Status */}
                          {item.reserve_price && !isEditing && (
                            <div className="mt-3 pt-3 border-t border-zinc-800">
                              <p className="font-mono text-xs text-zinc-500">
                                RESERVE: ${item.reserve_price?.toFixed(2)} 
                                {item.reserve_met ? (
                                  <span className="text-green-500 ml-2">✓ MET</span>
                                ) : (
                                  <span className="text-yellow-500 ml-2">NOT MET</span>
                                )}
                              </p>
                            </div>
                          )}

                          {/* Pending Auto-Bids */}
                          {autoBidsData[item.product_id]?.length > 0 && !isEditing && (
                            <div className="mt-3 pt-3 border-t border-zinc-800">
                              <p className="font-mono text-xs text-purple-400 mb-2">
                                ⚡ {autoBidsData[item.product_id].length} PENDING AUTO-BID{autoBidsData[item.product_id].length > 1 ? 'S' : ''}
                              </p>
                              <div className="space-y-1">
                                {autoBidsData[item.product_id].map((ab, idx) => (
                                  <div key={idx} className="flex items-center justify-between text-xs bg-purple-500/10 px-2 py-1">
                                    <span className="font-mono text-zinc-400 truncate max-w-[150px]" title={ab.user_email}>
                                      {ab.user_name || ab.user_email?.split('@')[0] || 'Unknown'}
                                    </span>
                                    <span className="font-mono text-purple-400 font-bold">
                                      UP TO ${ab.max_amount?.toFixed(2)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Edit Form */}
                          {isEditing && (
                            <div className="mt-4 p-3 md:p-4 bg-zinc-900 border border-zinc-700 space-y-3">
                              <p className="font-mono text-sm text-tiffany-500 mb-2">EDIT AUCTION</p>
                              
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                  <label className="font-mono text-xs text-zinc-500 block mb-1">RESERVE PRICE</label>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    value={editForm.reserve_price}
                                    onChange={(e) => setEditForm(prev => ({ ...prev, reserve_price: e.target.value }))}
                                    className="bg-zinc-800 border-zinc-700 text-white rounded-none h-10"
                                    placeholder="0.00"
                                  />
                                </div>
                                <div>
                                  <label className="font-mono text-xs text-zinc-500 block mb-1">BUY NOW PRICE</label>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    value={editForm.buy_now_price}
                                    onChange={(e) => setEditForm(prev => ({ ...prev, buy_now_price: e.target.value }))}
                                    className="bg-zinc-800 border-zinc-700 text-white rounded-none h-10"
                                    placeholder="0.00"
                                  />
                                </div>
                              </div>

                              <div>
                                <label className="font-mono text-xs text-zinc-500 block mb-1">AUCTION END TIME (EASTERN)</label>
                                <Input
                                  type="datetime-local"
                                  value={editForm.bid_end_time}
                                  onChange={(e) => setEditForm(prev => ({ ...prev, bid_end_time: e.target.value }))}
                                  className="bg-zinc-800 border-zinc-700 text-white rounded-none h-10"
                                />
                                {editForm.bid_end_time && (
                                  <p className="text-xs text-tiffany-500 mt-1">
                                    {new Date(editForm.bid_end_time).toLocaleString('en-US', {
                                      timeZone: 'America/New_York',
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric',
                                      hour: 'numeric',
                                      minute: '2-digit',
                                      hour12: true
                                    }).toUpperCase()} EST
                                  </p>
                                )}
                                <div className="flex flex-wrap gap-2 mt-2">
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    onClick={() => addTime(30)}
                                    className="text-xs border-zinc-700 rounded-none"
                                  >
                                    +30M
                                  </Button>
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    onClick={() => addTime(60)}
                                    className="text-xs border-zinc-700 rounded-none"
                                  >
                                    +1H
                                  </Button>
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    onClick={() => addTime(60 * 24)}
                                    className="text-xs border-zinc-700 rounded-none"
                                  >
                                    +24H
                                  </Button>
                                </div>
                              </div>

                              <div className="flex flex-wrap gap-2 pt-2">
                                <Button
                                  onClick={() => saveAuctionChanges(item.product_id)}
                                  className="bg-tiffany-500 text-black hover:bg-tiffany-400 rounded-none"
                                  size="sm"
                                >
                                  <Save className="w-4 h-4 mr-1" />
                                  SAVE
                                </Button>
                                <Button
                                  onClick={cancelEditing}
                                  variant="outline"
                                  className="border-zinc-700 rounded-none"
                                  size="sm"
                                >
                                  <X className="w-4 h-4 mr-2" />
                                  CANCEL
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t border-zinc-800">
                        {!isEditing && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => startEditing(item)}
                              className="text-zinc-400 hover:text-white border-zinc-700 rounded-none"
                            >
                              <Edit2 className="w-4 h-4 mr-2" />
                              EDIT AUCTION
                            </Button>
                            
                            {!isEnded && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => endAuctionNow(item.product_id, item.product_name)}
                                className="text-red-400 hover:text-red-300 border-red-500/30 hover:border-red-500/50 rounded-none"
                              >
                                <X className="w-4 h-4 mr-2" />
                                END NOW
                              </Button>
                            )}
                            
                            {isEnded && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => restartAuction(item)}
                                className="text-green-400 hover:text-green-300 border-green-500/30 hover:border-green-500/50 rounded-none"
                              >
                                <RefreshCw className="w-4 h-4 mr-2" />
                                RESTART AUCTION
                              </Button>
                            )}
                          </>
                        )}

                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleExpanded(item.product_id)}
                          className="text-zinc-400 hover:text-white ml-auto"
                        >
                          {isExpanded ? (
                            <><ChevronUp className="w-4 h-4 mr-2" /> HIDE BIDS</>
                          ) : (
                            <><ChevronDown className="w-4 h-4 mr-2" /> VIEW BIDS ({item.total_bids})</>
                          )}
                        </Button>
                      </div>
                    </div>
                    
                    {/* Bid History */}
                    {isExpanded && (
                      <div className="border-t border-zinc-800 bg-zinc-900/50 p-4 overflow-x-auto">
                        <p className="font-mono text-xs text-zinc-500 mb-3">BID HISTORY</p>
                        {item.all_bids && item.all_bids.length > 0 ? (
                          <div className="space-y-3">
                            {item.all_bids.map((bid, idx) => (
                              <div 
                                key={bid.id || idx} 
                                className={`flex flex-col sm:flex-row sm:items-center sm:justify-between py-2 border-b border-zinc-800/50 last:border-0 ${idx === 0 ? 'text-tiffany-500' : 'text-zinc-400'}`}
                              >
                                <div className="flex-1 min-w-0 mb-1 sm:mb-0">
                                  <span className="font-mono text-sm block sm:inline truncate">{bid.user_full_name || bid.bidder_name || 'Unknown'}</span>
                                  <span className="font-mono text-xs text-zinc-600 block sm:inline sm:ml-1 truncate">({bid.user_email || bid.bidder_email || 'Unknown'})</span>
                                </div>
                                <div className="flex items-center gap-2 sm:text-right flex-shrink-0">
                                  <span className="font-mono font-bold">${bid.amount?.toFixed(2)}</span>
                                  <span className="font-mono text-xs text-zinc-600 whitespace-nowrap">
                                    {new Date(bid.created_at).toLocaleString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                      hour: 'numeric',
                                      minute: '2-digit'
                                    })}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="font-mono text-sm text-zinc-600">NO BIDS YET</p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          </div>

          {/* Needs Re-auction Section */}
          {needsReauctionData.length > 0 && (
            <div className="mt-8">
              <h2 className="font-mono text-lg text-yellow-500 mb-4">NEEDS RE-AUCTION ({needsReauctionData.length})</h2>
              
              <p className="font-mono text-sm text-zinc-500 mb-4">
                THESE ITEMS FAILED TO SELL (RESERVE NOT MET, NO BIDS, OR ORDER CANCELLED) AND ARE CURRENTLY UNAVAILABLE UNTIL RE-AUCTIONED.
              </p>
              
              <div className="space-y-4">
                {needsReauctionData.map((item) => (
                  <div key={item.id} className="border border-yellow-500/30 bg-yellow-500/5 p-4">
                    <div className="flex items-start gap-4">
                      {item.image_url && (
                        <img 
                          src={item.image_url} 
                          alt={item.name}
                          className="w-20 h-20 object-cover grayscale"
                        />
                      )}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <Link 
                            to={`/product/${item.id}`}
                            className="font-mono text-lg text-white hover:text-tiffany-500"
                          >
                            {item.name}
                          </Link>
                          <Badge className="bg-yellow-500/20 text-yellow-500 rounded-none border-0">
                            RESERVE NOT MET
                          </Badge>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                          <div>
                            <p className="font-mono text-xs text-zinc-500">STARTING PRICE</p>
                            <p className="font-mono text-white">${item.price?.toFixed(2)}</p>
                          </div>
                          <div>
                            <p className="font-mono text-xs text-zinc-500">PREV RESERVE</p>
                            <p className="font-mono text-white">${item.reserve_price?.toFixed(2) || 'N/A'}</p>
                          </div>
                          <div>
                            <p className="font-mono text-xs text-zinc-500">HIGHEST BID</p>
                            <p className="font-mono text-white">${item.current_bid?.toFixed(2) || '0.00'}</p>
                          </div>
                          <div>
                            <p className="font-mono text-xs text-zinc-500">STOCK</p>
                            <p className="font-mono text-white">{item.stock}</p>
                          </div>
                        </div>

                        {/* Re-auction Form */}
                        <div className="mt-4 pt-4 border-t border-yellow-500/20">
                          <p className="font-mono text-xs text-yellow-500 mb-3">RE-AUCTION SETTINGS</p>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <div>
                              <label className="font-mono text-xs text-zinc-500 block mb-1">DURATION (HRS)</label>
                              <Input
                                type="number"
                                value={reauctionForm[item.id]?.hours || 24}
                                onChange={(e) => setReauctionForm(prev => ({
                                  ...prev,
                                  [item.id]: { ...prev[item.id], hours: e.target.value }
                                }))}
                                className="bg-zinc-800 border-zinc-700 text-white rounded-none h-9"
                              />
                            </div>
                            <div>
                              <label className="font-mono text-xs text-zinc-500 block mb-1">NEW RESERVE</label>
                              <Input
                                type="number"
                                step="0.01"
                                placeholder={item.reserve_price?.toFixed(2) || '0.00'}
                                value={reauctionForm[item.id]?.reserve_price || ''}
                                onChange={(e) => setReauctionForm(prev => ({
                                  ...prev,
                                  [item.id]: { ...prev[item.id], reserve_price: e.target.value }
                                }))}
                                className="bg-zinc-800 border-zinc-700 text-white rounded-none h-9"
                              />
                            </div>
                            <div>
                              <label className="font-mono text-xs text-zinc-500 block mb-1">NEW BIN</label>
                              <Input
                                type="number"
                                step="0.01"
                                placeholder="Buy Now"
                                value={reauctionForm[item.id]?.buy_now_price || ''}
                                onChange={(e) => setReauctionForm(prev => ({
                                  ...prev,
                                  [item.id]: { ...prev[item.id], buy_now_price: e.target.value }
                                }))}
                                className="bg-zinc-800 border-zinc-700 text-white rounded-none h-9"
                              />
                            </div>
                            <div className="flex items-end">
                              <Button
                                onClick={() => handleReauction(item.id)}
                                className="w-full bg-yellow-500 text-black hover:bg-yellow-400 rounded-none h-9"
                              >
                                <RefreshCw className="w-4 h-4 mr-2" />
                                RE-AUCTION
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
// Build trigger: 1771127423
